// src/firebase/firebaseService.js - Updated with Batch Operations
import { 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { db, storage } from './config';
import { getDistance } from 'geolib';
import { mirrorItemsFull, mirrorServicesFull, buildShopFields } from './itemsCollectionSync';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  increment,
  writeBatch,
  arrayRemove,
  arrayUnion,
  addDoc,
  orderBy,
  limit,
  collectionGroup,
  documentId,
} from 'firebase/firestore';

/**
 * Upload community media (image or video) to Firebase Storage
 */
export const uploadCommunityMedia = async (shopId, file, type, ownerId = null) => {
  try {
    const { compressImage } = await import('../utils/imageCompression');
    
    // Validate file
    if (type === 'clip') {
      // Check video duration (<10 seconds)
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      const duration = await new Promise((resolve, reject) => {
        video.onloadedmetadata = () => {
          window.URL.revokeObjectURL(video.src);
          resolve(video.duration);
        };
        video.onerror = () => reject(new Error('Invalid video file'));
        video.src = URL.createObjectURL(file);
      });
      
      if (duration > 10) {
        throw new Error('Video must be 10 seconds or less');
      }
    }
    
    // Compress if image
    let fileToUpload = file;
    if (type === 'image') {
      fileToUpload = await compressImage(file);
    }
    
    // Upload to Firebase Storage
    const timestamp = Date.now();
    const fileName = `${type}-${timestamp}`;
    const storageRef = ref(
      storage, 
      `shops/${shopId}/community/${fileName}`
    );
    
    const metadata = {
      contentType: file.type,
      cacheControl: 'public, max-age=31536000',
      ...(ownerId ? { customMetadata: { ownerId } } : {})
    };

    const snapshot = await uploadBytes(storageRef, fileToUpload, metadata);
    const downloadURL = await getDownloadURL(snapshot.ref);

    console.log('✅ Community media uploaded:', downloadURL);
    return downloadURL;
  } catch (error) {
    console.error('❌ Community media upload failed:', error);
    throw error;
  }
};

/**
 * Upload a small thumbnail for a community post/event's first image,
 * alongside the main image uploaded via uploadCommunityMedia.
 */
export const uploadCommunityThumbnail = async (shopId, thumbnailFile, ownerId = null) => {
  try {
    const timestamp = Date.now();
    const storageRef = ref(
      storage,
      `shops/${shopId}/community/thumb-${timestamp}`
    );

    const metadata = {
      contentType: thumbnailFile.type,
      cacheControl: 'public, max-age=31536000',
      ...(ownerId ? { customMetadata: { ownerId } } : {})
    };

    const snapshot = await uploadBytes(storageRef, thumbnailFile, metadata);
    const downloadURL = await getDownloadURL(snapshot.ref);

    console.log('✅ Community thumbnail uploaded:', downloadURL);
    return downloadURL;
  } catch (error) {
    console.error('❌ Community thumbnail upload failed:', error);
    throw error;
  }
};

/**
 * Upload product/service media (image or video) to Firebase Storage
 */
export const uploadProductMedia = async (shopId, itemId, file, isService = false, ownerId = null) => {
  try {
    const isVideo = file.type.startsWith('video/');
    
    // Validate video
    if (isVideo) {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      const duration = await new Promise((resolve, reject) => {
        video.onloadedmetadata = () => {
          window.URL.revokeObjectURL(video.src);
          resolve(video.duration);
        };
        video.onerror = () => reject(new Error('Invalid video file'));
        video.src = URL.createObjectURL(file);
      });
      
      if (duration > 10) {
        throw new Error('Video must be 10 seconds or less');
      }
    }
    
    // Images arrive here already compressed (canvas-based compression runs
    // when the file is first selected, before it's queued for upload) — a
    // second FileReader-based compression pass on mobile was unreliable and
    // threw "File reading failed", so it's skipped here.
    const fileToUpload = file;

    // Upload to Firebase Storage
    const timestamp = Date.now();
    const fileType = isVideo ? 'video' : 'image';
    const folder = isService ? 'services' : 'items';
    const fileName = `${fileType}-${timestamp}`;
    const storageRef = ref(
      storage, 
      `shops/${shopId}/${folder}/${itemId}/${fileName}`
    );
    
    const metadata = {
      contentType: file.type,
      cacheControl: 'public, max-age=31536000',
      ...(ownerId ? { customMetadata: { ownerId } } : {})
    };

    const snapshot = await uploadBytes(storageRef, fileToUpload, metadata);
    const downloadURL = await getDownloadURL(snapshot.ref);

    console.log(`✅ ${fileType} uploaded for ${folder}:`, downloadURL);
    return downloadURL;
  } catch (error) {
    console.error('❌ Product/Service media upload failed:', error);
    throw error;
  }
};

/**
 * Upload a small grid thumbnail for a product/service image, alongside the
 * main image uploaded via uploadProductMedia. Images only (thumbnails are
 * never generated for video).
 */
export const uploadProductThumbnail = async (shopId, itemId, thumbnailFile, isService = false, ownerId = null) => {
  try {
    const timestamp = Date.now();
    const folder = isService ? 'services' : 'items';
    const storageRef = ref(
      storage,
      `shops/${shopId}/${folder}/${itemId}/thumb-${timestamp}`
    );

    const metadata = {
      contentType: thumbnailFile.type,
      cacheControl: 'public, max-age=31536000',
      ...(ownerId ? { customMetadata: { ownerId } } : {})
    };

    const snapshot = await uploadBytes(storageRef, thumbnailFile, metadata);
    const downloadURL = await getDownloadURL(snapshot.ref);

    console.log(`✅ thumbnail uploaded for ${folder}:`, downloadURL);
    return downloadURL;
  } catch (error) {
    console.error('❌ Thumbnail upload failed:', error);
    throw error;
  }
};

/**
 * Upload a digital product file to private Firebase Storage path.
 * Returns the storage PATH (not a download URL) — access is via signed URL from backend.
 */
export const uploadDigitalProduct = async (shopId, itemId, file, ownerId = null) => {
  try {
    const timestamp = Date.now();
    const storageRef = ref(
      storage,
      `shops/${shopId}/digital-products/${itemId}/${timestamp}-${file.name}`
    );
    const metadata = {
      contentType: file.type,
      ...(ownerId ? { customMetadata: { ownerId } } : {})
    };
    const snapshot = await uploadBytes(storageRef, file, metadata);
    console.log('✅ Digital product uploaded:', snapshot.ref.fullPath);
    return snapshot.ref.fullPath;
  } catch (error) {
    console.error('❌ Digital product upload failed:', error);
    throw error;
  }
};

/**
 * Save community post to Firestore subcollection
 */
export const saveCommunityPost = async (shopId, postData) => {
  try {
    const shopRef = doc(db, 'shops', shopId);
    const shopSnap = await getDoc(shopRef);
    
    if (!shopSnap.exists()) {
      throw new Error('Shop not found');
    }
    
    const shopData = shopSnap.data();
    
    // Create post in subcollection
    const postsRef = collection(db, 'shops', shopId, 'communityPosts');
    const newPost = {
      ...postData,
      shopId,
      shopName: shopData.name,
      shopProfile: shopData.profile,
      shopTheme: shopData.theme,
      engagement: {
        likes: [],
        likeCount: 0,
        views: 0
      },
      createdAt: new Date().toISOString(),
      deleted: false
    };
    
    const docRef = await addDoc(postsRef, newPost);
    console.log('✅ Community post saved:', docRef.id);
    
    return { id: docRef.id, ...newPost };
  } catch (error) {
    console.error('❌ Error saving community post:', error);
    throw error;
  }
};

/**
 * Get community posts for a specific shop
 */
export const getCommunityPostsForShop = async (shopId, limitCount = null) => {
  try {
    const postsRef = collection(db, 'shops', shopId, 'communityPosts');
    const constraints = [
      where('deleted', '==', false),
      orderBy('createdAt', 'desc'),
    ];
    if (typeof limitCount === 'number') constraints.push(limit(limitCount));
    const q = query(postsRef, ...constraints);

    const snapshot = await getDocs(q);
    const posts = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`📋 Loaded ${posts.length} posts for shop ${shopId}`);
    return posts;
  } catch (error) {
    console.error('❌ Error loading community posts:', error);
    return [];
  }
};

/**
 * Toggle like on a community post
 */
export const toggleCommunityPostLike = async (shopId, postId, userId) => {
  if (!userId) {
    throw new Error('Authentication required to like posts');
  }
  
  try {
    const postRef = doc(db, 'shops', shopId, 'communityPosts', postId);
    const postSnap = await getDoc(postRef);
    
    if (!postSnap.exists()) {
      throw new Error('Post not found');
    }
    
    const postData = postSnap.data();
    const likes = postData.engagement?.likes || [];
    const isLiked = likes.includes(userId);
    
    let updatedLikes;
    if (isLiked) {
      updatedLikes = likes.filter(id => id !== userId);
    } else {
      updatedLikes = [...likes, userId];
    }
    
    await updateDoc(postRef, {
      'engagement.likes': updatedLikes,
      'engagement.likeCount': updatedLikes.length
    });
    
    return { isLiked: !isLiked, likeCount: updatedLikes.length };
  } catch (error) {
    console.error('❌ Error toggling post like:', error);
    throw error;
  }
};

/**
 * Update: Get posts from followed shops (using subcollections)
 */
export const getFollowedShopsPosts = async (userId, limitCount = 20) => {
  try {
    const userShopRef = doc(db, 'shops', userId);
    const userShop = await getDoc(userShopRef);
    
    if (!userShop.exists()) return [];
    
    const following = userShop.data().following || [];
    if (following.length === 0) return [];

    // Fetch posts from each followed shop's subcollection in parallel instead
    // of one shop at a time — same reads, just concurrent round trips.
    const perShopPosts = await Promise.all(following.map(async (followedShopId) => {
      try {
        const followedShopSnap = await getDoc(doc(db, 'shops', followedShopId));
        const shopData = followedShopSnap.exists() ? followedShopSnap.data() : {};
        const postsRef = collection(db, 'shops', followedShopId, 'communityPosts');
        const q = query(
          postsRef,
          where('deleted', '==', false),
          orderBy('createdAt', 'desc'),
          limit(10) // Max 10 posts per shop
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          // Override with the shop's current name/pic — see getPopularPostsInArea
          // for why (posts otherwise keep showing whatever was true when created).
          shopId: followedShopId,
          shopName: shopData.name || 'Unknown Shop',
          shopProfile: shopData.profile || null,
          shopUsername: shopData.username || null,
        }));
      } catch (error) {
        console.warn(`⚠️ Error fetching posts from shop ${followedShopId}:`, error);
        return [];
      }
    }));

    const allPosts = perShopPosts.flat();

    // Sort by createdAt descending
    allPosts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    return allPosts.slice(0, limitCount);
  } catch (error) {
    console.error('❌ Error getting followed posts:', error);
    return [];
  }
};

/**
 * Update: Get popular posts in area (using subcollections)
 */
export const getPopularPostsInArea = async (userLocation, radius = 50, limitCount = 20) => {
  try {
    const shopsSnapshot = await getDocs(collection(db, 'shops'));

    // Fetch posts from each shop's subcollection in parallel instead of one
    // shop at a time — same reads, just concurrent round trips.
    const perShopPosts = await Promise.all(shopsSnapshot.docs.map(async (shopDoc) => {
      try {
        const shopData = shopDoc.data();
        const postsRef = collection(db, 'shops', shopDoc.id, 'communityPosts');
        const q = query(
          postsRef,
          where('deleted', '==', false),
          orderBy('createdAt', 'desc'),
          limit(5)
        );

        const postsSnapshot = await getDocs(q);
        const posts = postsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          // Override with the shop's current name/pic — the post document only
          // carries whatever was denormalized onto it when it was created, so
          // without this a renamed/re-photographed shop's old posts would keep
          // showing the stale name/picture forever.
          shopId: shopDoc.id,
          shopName: shopData.name || 'Unknown Shop',
          shopProfile: shopData.profile || null,
          shopUsername: shopData.username || null,
        }));

        // Filter by distance if coordinates available
        const shopResults = [];
        posts.forEach(post => {
          if (userLocation && post.coordinates?.lat && post.coordinates?.lng) {
            try {
              const distanceInMeters = getDistance(
                { latitude: userLocation.latitude, longitude: userLocation.longitude },
                { latitude: post.coordinates.lat, longitude: post.coordinates.lng }
              );
              const distanceInMiles = distanceInMeters / 1609.34;

              if (distanceInMiles <= radius) {
                shopResults.push({
                  ...post,
                  distance: distanceInMeters,
                  distanceInMiles: distanceInMiles.toFixed(1)
                });
              }
            } catch (e) {
              console.warn('⚠️ Error calculating distance:', e);
            }
          } else {
            // Include posts without location
            shopResults.push(post);
          }
        });
        return shopResults;
      } catch (error) {
        console.warn(`⚠️ Error fetching posts from shop ${shopDoc.id}:`, error);
        return [];
      }
    }));

    const allPosts = perShopPosts.flat();

    // Sort by engagement score
    allPosts.sort((a, b) => {
      const scoreA = (a.engagement?.views || 0) + 
                     (a.engagement?.likeCount || 0) * 2;
      const scoreB = (b.engagement?.views || 0) + 
                     (b.engagement?.likeCount || 0) * 2;
      return scoreB - scoreA;
    });
    
    return allPosts.slice(0, limitCount);
  } catch (error) {
    console.error('❌ Error getting popular posts:', error);
    return [];
  }
};

/**
 * Delete community post
 */
export const deleteCommunityPost = async (shopId, postId) => {
  try {
    const postRef = doc(db, 'shops', shopId, 'communityPosts', postId);
    await updateDoc(postRef, { deleted: true });
    console.log('✅ Post marked as deleted:', postId);
  } catch (error) {
    console.error('❌ Error deleting post:', error);
    throw error;
  }
};

/**
 * Increment post views
 */
export const incrementPostViews = async (shopId, postId) => {
  try {
    const postRef = doc(db, 'shops', shopId, 'communityPosts', postId);
    await updateDoc(postRef, {
      'engagement.views': increment(1)
    });
  } catch (error) {
    console.error('❌ Error incrementing views:', error);
  }
};

// ============= NEW USERNAME FUNCTIONS =============

/**
 * Generate a unique username from shop name
 * @param {string} shopName 
 * @returns {Promise<string>}
 */
export const generateUsername = async (shopName, excludeShopId = null) => {
  if (!shopName) return null;

  // Convert to lowercase and remove all spaces and special characters
  // CHANGED: No hyphens, just concatenate words
  let baseUsername = shopName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Remove everything except letters and numbers
    .substring(0, 30); // Limit length

  // If empty after cleaning, use a default
  if (!baseUsername) {
    baseUsername = 'shop';
  }

  // Check if base username is available
  const isAvailable = !(await usernameExists(baseUsername, excludeShopId));

  if (isAvailable) {
    return baseUsername;
  }

  // If taken, append random numbers (no hyphens)
  let attempts = 0;
  while (attempts < 10) {
    const randomNum = Math.floor(Math.random() * 9999);
    const candidateUsername = `${baseUsername}${randomNum}`; // CHANGED: No hyphen

    const exists = await usernameExists(candidateUsername, excludeShopId);
    if (!exists) {
      return candidateUsername;
    }
    attempts++;
  }

  // Fallback with timestamp (no hyphens)
  return `${baseUsername}${Date.now().toString().slice(-6)}`; // CHANGED: No hyphen
};

/**
 * Check if username already exists
 * @param {string} username
 * @param {string|null} excludeShopId - shop doc id to ignore (e.g. the shop
 *   being renamed itself, so its own current username isn't seen as "taken")
 * @returns {Promise<boolean>}
 */
export const usernameExists = async (username, excludeShopId = null) => {
  try {
    const shopsRef = collection(db, 'shops');
    const q = query(shopsRef, where('username', '==', username));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return false;
    if (excludeShopId) {
      return querySnapshot.docs.some(doc => doc.id !== excludeShopId);
    }
    return true;
  } catch (error) {
    console.error('Error checking username:', error);
    return false;
  }
};

// src/firebase/firebaseService.js

/**
 * Toggle like on an item (authenticated users only)
 */
export const toggleItemLike = async (shopId, itemId, userId, isService = false) => {
  if (!userId) {
    throw new Error('Authentication required to like items');
  }

  try {
    const engagementRef = doc(db, 'shops', shopId, 'engagement', itemId);
    const engagementSnap = await getDoc(engagementRef);
    
    let likes = [];
    let isLiked = false;
    
    if (engagementSnap.exists()) {
      likes = engagementSnap.data().likes || [];
      const likeIndex = likes.indexOf(userId);
      
      if (likeIndex > -1) {
        // Unlike - remove user ID
        likes.splice(likeIndex, 1);
        isLiked = false;
      } else {
        // Like - add user ID
        likes.push(userId);
        isLiked = true;
      }
      
      await updateDoc(engagementRef, {
        likes,
        likeCount: likes.length,
        updatedAt: new Date().toISOString()
      });
    } else {
      // Create new engagement document
      likes = [userId];
      isLiked = true;
      
      await setDoc(engagementRef, {
        itemId,
        shopId,
        isService,
        likes,
        likeCount: 1,
        viewCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    
    console.log(`Like toggled for item ${itemId}:`, { isLiked, likeCount: likes.length });
    return { isLiked, likeCount: likes.length };
  } catch (error) {
    console.error('Error toggling like:', error);
    throw error;
  }
};

/**
 * Toggle like on a shop (authenticated users only)
 * Uses shops/{shopId}/engagement/__shop__ doc
 */
export const toggleShopLike = async (shopId, userId) => {
  if (!userId) throw new Error('Authentication required');
  try {
    const engagementRef = doc(db, 'shops', shopId, 'engagement', '__shop__');
    const engagementSnap = await getDoc(engagementRef);
    let likes = [];
    if (engagementSnap.exists()) {
      likes = engagementSnap.data().likes || [];
      const idx = likes.indexOf(userId);
      if (idx > -1) likes.splice(idx, 1);
      else likes.push(userId);
      await updateDoc(engagementRef, { likes, likeCount: likes.length, updatedAt: new Date().toISOString() });
    } else {
      likes = [userId];
      await setDoc(engagementRef, { shopId, likes, likeCount: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
    }
    return { isLiked: likes.includes(userId), likeCount: likes.length };
  } catch (error) {
    console.error('Error toggling shop like:', error);
    throw error;
  }
};

/**
 * Get shop-level engagement (likes) for a list of shopIds
 */
export const getShopsEngagement = async (shopIds, userId = null) => {
  if (!shopIds || shopIds.length === 0) return {};
  try {
    const results = {};
    await Promise.all(shopIds.map(async (shopId) => {
      const engagementRef = doc(db, 'shops', shopId, 'engagement', '__shop__');
      const snap = await getDoc(engagementRef);
      if (snap.exists()) {
        const data = snap.data();
        results[shopId] = {
          likeCount: data.likeCount || 0,
          isLiked: userId ? (data.likes || []).includes(userId) : false,
        };
      } else {
        results[shopId] = { likeCount: 0, isLiked: false };
      }
    }));
    return results;
  } catch (error) {
    console.error('Error fetching shops engagement:', error);
    return {};
  }
};

// In-memory queue: accumulates view increments during a session
const pendingViews = new Map();

export const queueViewIncrement = (shopId, itemId, isService = false) => {
  if (!shopId || !itemId) return;
  const key = `${shopId}:${itemId}`;
  const existing = pendingViews.get(key);
  if (existing) {
    existing.count += 1;
  } else {
    pendingViews.set(key, { shopId, itemId, isService, count: 1 });
  }
};

export const flushPendingViews = async () => {
  if (pendingViews.size === 0) return;
  const entries = [...pendingViews.values()];
  pendingViews.clear();
  // Chunk into batches of 500 (Firestore batch limit)
  for (let i = 0; i < entries.length; i += 500) {
    const chunk = entries.slice(i, i + 500);
    const batch = writeBatch(db);
    chunk.forEach(({ shopId, itemId, count }) => {
      const ref = doc(db, 'shops', shopId, 'engagement', itemId);
      batch.set(ref, { viewCount: increment(count), updatedAt: new Date().toISOString() }, { merge: true });
    });
    await batch.commit();
  }
};

/**
 * Get engagement data for multiple items (for a single shop)
 */
export const getItemsEngagement = async (shopId, items, userId = null) => {
  if (!items || items.length === 0) return items;
  
  try {
    const engagementPromises = items.map(async (item) => {
      const engagementRef = doc(db, 'shops', shopId, 'engagement', item.id);
      const engagementSnap = await getDoc(engagementRef);
      
      if (engagementSnap.exists()) {
        const engagement = engagementSnap.data();
        return {
          ...item,
          isLiked: userId ? (engagement.likes || []).includes(userId) : false,
          likeCount: engagement.likeCount || 0,
          viewCount: engagement.viewCount || 0
        };
      }
      
      return {
        ...item,
        isLiked: false,
        likeCount: 0,
        viewCount: 0
      };
    });
    
    return await Promise.all(engagementPromises);
  } catch (error) {
    console.error('Error getting items engagement:', error);
    return items.map(item => ({
      ...item,
      isLiked: false,
      likeCount: 0,
      viewCount: 0
    }));
  }
};

/**
 * Get engagement data for items across multiple shops (for welcome page)
 */
export const getMultiShopItemsEngagement = async (items, userId = null) => {
  if (!items || items.length === 0) return items;

  try {
    // Group items by shopId so we can batch-fetch per subcollection
    const byShop = {};
    items.forEach((item) => {
      if (item.shopId && item.id) {
        if (!byShop[item.shopId]) byShop[item.shopId] = [];
        byShop[item.shopId].push(item.id);
      }
    });

    // Fetch engagement docs in chunks of 30 (Firestore 'in' limit) per shop
    const engagementMap = {};
    await Promise.all(
      Object.entries(byShop).map(async ([shopId, ids]) => {
        for (let i = 0; i < ids.length; i += 30) {
          const chunk = ids.slice(i, i + 30);
          try {
            const snap = await getDocs(
              query(
                collection(db, 'shops', shopId, 'engagement'),
                where(documentId(), 'in', chunk)
              )
            );
            snap.forEach(d => {
              engagementMap[`${shopId}:${d.id}`] = d.data();
            });
          } catch (err) {
            console.warn(`Error fetching engagement for shop ${shopId}:`, err);
          }
        }
      })
    );

    return items.map(item => {
      const eng = engagementMap[`${item.shopId}:${item.id}`];
      if (eng) {
        return {
          ...item,
          isLiked: userId ? (eng.likes || []).includes(userId) : false,
          likeCount: eng.likeCount || 0,
          viewCount: eng.viewCount || 0,
        };
      }
      return { ...item, isLiked: false, likeCount: 0, viewCount: 0 };
    });
  } catch (error) {
    console.error('Error getting multi-shop items engagement:', error);
    return items.map(item => ({ ...item, isLiked: false, likeCount: 0, viewCount: 0 }));
  }
};

/**
 * Batch increment views for multiple items
 */
export const batchIncrementViews = async (items) => {
  if (!items || items.length === 0) return;
  
  try {
    items.forEach(item => {
      if (item.shopId && item.id) {
        queueViewIncrement(item.shopId, item.id, item.isService);
      }
    });
    await flushPendingViews();
  } catch (error) {
    console.error('Error batch incrementing views:', error);
  }
};


/**
 * Get user's IP address
 */
export const getUserIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error getting IP:', error);
    return 'unknown';
  }
};


/**
 * Track view for an item (once per IP, using subcollection)
 */
export const trackItemView = async (shopId, itemId, userIP, isService = false) => {
  if (!userIP || userIP === 'unknown') {
    console.warn('Valid IP required for view tracking');
    return;
  }

  try {
    const engagementRef = doc(db, 'shops', shopId, 'engagement', itemId);
    const engagementSnap = await getDoc(engagementRef);
    
    let views = [];
    
    if (engagementSnap.exists()) {
      views = engagementSnap.data().views || [];
      
      // Check if this IP already viewed
      if (views.includes(userIP)) {
        console.log('IP already viewed this item');
        return;
      }
      
      // Add IP to views
      views.push(userIP);
      
      await updateDoc(engagementRef, {
        views,
        viewCount: views.length,
        updatedAt: new Date().toISOString()
      });
    } else {
      // Create new engagement document with first view
      views = [userIP];
      
      await setDoc(engagementRef, {
        itemId,
        shopId,
        isService,
        likes: [],
        likeCount: 0,
        views,
        viewCount: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    
    console.log(`View tracked for item ${itemId} by IP ${userIP}`);
  } catch (error) {
    console.error('Error tracking view:', error);
  }
};

/**
 * Get event posts from community posts across all shops
 * @param {string|null} userId - Current user ID (for personalized results)
 * @param {Object|null} userLocation - User's location {latitude, longitude}
 * @param {number} resultLimit - Maximum number of events to return
 * @returns {Promise<Array>} Array of event posts
 */
export const getEventPosts = async (userId = null, userLocation = null, resultLimit = 20) => {
  try {
    console.log('🎉 Fetching event posts...', { userId, hasLocation: !!userLocation });
    
    const shopsSnapshot = await getDocs(collection(db, 'shops'));

    let allEvents = [];
    let userOwnEvents = [];
    let followedEvents = [];
    
    // Get user's following list if authenticated
    let followingList = [];
    if (userId) {
      try {
        const userShopRef = doc(db, 'shops', userId);
        const userShop = await getDoc(userShopRef);
        if (userShop.exists()) {
          followingList = userShop.data().following || [];
        }
      } catch (error) {
        console.warn('Could not get following list:', error);
      }
    }
    
    // Fetch each shop's community posts subcollection in parallel instead of
    // one shop at a time — same reads/filters, just concurrent round trips.
    const eligibleShopDocs = shopsSnapshot.docs.filter(shopDoc => {
      const shopData = shopDoc.data();
      if (shopData.accountStatus === 'paused' || shopData.accountStatus === 'deleted') return false;
      if (shopData.storeOpen === false) return false;
      return true;
    });

    const perShopEvents = await Promise.all(eligibleShopDocs.map(async (shopDoc) => {
      const shopData = shopDoc.data();
      const shopOwn = [];
      const shopFollowed = [];
      const shopOther = [];

      try {
        // Query for events in communityPosts subcollection — bounded to the
        // most recent 60 non-deleted posts per shop instead of reading the
        // entire subcollection, since events are inherently time-relevant
        // content shops post close to the date. Same query shape as
        // getFollowedShopsPosts/getPopularPostsInArea, so no new composite
        // index is needed.
        const postsRef = collection(db, 'shops', shopDoc.id, 'communityPosts');
        const postsQuery = query(postsRef, where('deleted', '==', false), orderBy('createdAt', 'desc'), limit(60));
        const postsSnapshot = await getDocs(postsQuery);

        // Filter in JavaScript for both 'type' and 'isEvent' fields
        postsSnapshot.docs.forEach(postDoc => {
          const postData = postDoc.data();

          // Skip deleted posts
          if (postData.deleted === true) return;

          // ✅ Check for BOTH 'type' and 'isEvent' fields for backwards compatibility
          const isEventPost = postData.type === 'event' || postData.isEvent === true;
          if (!isEventPost) return;

          // ✅ CRITICAL: Build contentUrl from multiple possible sources
          let eventMedia = null;

          // Priority order: contentUrl -> imageUrl -> first image in images array
          if (postData.contentUrl) {
            eventMedia = postData.contentUrl;
          } else if (postData.imageUrl) {
            eventMedia = postData.imageUrl;
          } else if (postData.images && Array.isArray(postData.images) && postData.images.length > 0) {
            // Find first non-null image
            eventMedia = postData.images.find(img => img && (typeof img === 'string' || img.url));
            // If it's an object with url property, extract url
            if (eventMedia && typeof eventMedia === 'object' && eventMedia.url) {
              eventMedia = eventMedia.url;
            }
          }

          const event = {
            id: postDoc.id,
            ...postData,
            contentUrl: eventMedia, // ✅ Ensure this exists
            shopId: shopDoc.id,
            shopName: shopData.name || 'Unknown Shop',
            shopProfile: shopData.profile || null,
            shopUsername: shopData.username || null,
            shopTheme: shopData.theme || {},
            currency: shopData.currency || 'USD'
          };

          // Categorize events
          if (userId && shopDoc.id === userId) {
            shopOwn.push(event);
          } else if (userId && followingList.includes(shopDoc.id)) {
            shopFollowed.push(event);
          } else {
            shopOther.push(event);
          }
        });
      } catch (error) {
        console.warn(`⚠️ Error fetching events from shop ${shopDoc.id}:`, error);
      }

      return { shopOwn, shopFollowed, shopOther };
    }));

    for (const { shopOwn, shopFollowed, shopOther } of perShopEvents) {
      userOwnEvents.push(...shopOwn);
      followedEvents.push(...shopFollowed);
      allEvents.push(...shopOther);
    }

    // Calculate distances if location provided
    const calculateDistance = (event) => {
      if (userLocation && event.coordinates?.lat && event.coordinates?.lng) {
        try {
          const distanceInMeters = getDistance(
            { latitude: userLocation.latitude, longitude: userLocation.longitude },
            { latitude: event.coordinates.lat, longitude: event.coordinates.lng }
          );
          const distanceInMiles = (distanceInMeters / 1609.34).toFixed(1);
          
          // Extract city from event location
          let itemCity = null;
          if (event.location && typeof event.location === 'string') {
            const cityMatch = event.location.split(',')[0].trim();
            itemCity = cityMatch || null;
          } else if (event.city) {
            itemCity = event.city;
          }
          
          return {
            ...event,
            distance: distanceInMeters,
            distanceInMiles,
            formattedDistance: `${distanceInMiles} mi`,
            itemCity
          };
        } catch (e) {
          console.warn('Error calculating distance for event:', e);
          return { ...event, distance: Infinity };
        }
      }
      return { ...event, distance: Infinity };
    };
    
    // Apply distance calculation to all event categories
    userOwnEvents = userOwnEvents.map(calculateDistance);
    followedEvents = followedEvents.map(calculateDistance);
    allEvents = allEvents.map(calculateDistance);
    
    // Sort by appropriate criteria
    if (userLocation) {
      // Sort by proximity (closest first)
      const sortByDistance = (a, b) => {
        const distA = a.distance !== undefined ? a.distance : Infinity;
        const distB = b.distance !== undefined ? b.distance : Infinity;
        
        if (distA === Infinity && distB === Infinity) return 0;
        if (distA === Infinity) return 1;
        if (distB === Infinity) return -1;
        
        return distA - distB;
      };
      
      userOwnEvents.sort(sortByDistance);
      followedEvents.sort(sortByDistance);
      allEvents.sort(sortByDistance);
    } else {
      // Sort by event date (soonest first)
      const sortByDate = (a, b) => {
        const dateA = a.eventDate ? new Date(a.eventDate) : new Date(8640000000000000);
        const dateB = b.eventDate ? new Date(b.eventDate) : new Date(8640000000000000);
        return dateA - dateB;
      };
      
      userOwnEvents.sort(sortByDate);
      followedEvents.sort(sortByDate);
      allEvents.sort(sortByDate);
    }
    
    // Combine: User's events → Followed events → Nearby events
    const combinedEvents = [
      ...userOwnEvents,
      ...followedEvents,
      ...allEvents
    ];
    
    // Engagement is already embedded in each communityPost document — no re-fetch needed
    let finalEvents = combinedEvents.slice(0, resultLimit);
    finalEvents = finalEvents.map(event => ({
      ...event,
      engagement: event.engagement || { likes: [], likeCount: 0, views: 0 },
      isLiked: userId ? (event.engagement?.likes?.includes(userId) || false) : false,
    }));

    // Legacy shape kept for non-authenticated path (no-op now, same result)
    if (!userId) {
      finalEvents = finalEvents.map(event => ({
        ...event,
        engagement: event.engagement || { likes: [], likeCount: 0, views: 0 },
        isLiked: false
      }));
    }
    
    // ✅ FIX: Transform events to ensure proper image structure (same as getShopEvents)
    const eventsWithImages = finalEvents.map(event => {
      // If event has contentUrl but no proper images array, create one
      if (event.contentUrl && (!event.images || event.images.every(img => !img))) {
        return {
          ...event,
          images: [event.contentUrl, null, null],
          currentImageIndex: 0
        };
      }
      
      // If event has images array but no contentUrl, set contentUrl from first image
      if (!event.contentUrl && event.images && event.images[0]) {
        const firstImage = event.images[0];
        const imageUrl = typeof firstImage === 'string' ? firstImage : firstImage?.url || null;
        
        return {
          ...event,
          contentUrl: imageUrl,
          currentImageIndex: event.currentImageIndex || 0
        };
      }
      
      // If event already has both, keep them
      return {
        ...event,
        currentImageIndex: event.currentImageIndex || 0
      };
    });
    
    console.log(`✅ Loaded ${eventsWithImages.length} event posts`, {
      userOwn: userOwnEvents.length,
      followed: followedEvents.length,
      nearby: allEvents.length,
      totalEvents: combinedEvents.length,
      withMedia: eventsWithImages.filter(e => e.contentUrl).length,
      withoutMedia: eventsWithImages.filter(e => !e.contentUrl).length
    });
    
    return eventsWithImages;
  } catch (error) {
    console.error('❌ Error fetching event posts:', error);
    return [];
  }
};

/**
 * Get all events for a specific shop
 * @param {string} shopId - Shop ID
 * @returns {Promise<Array>} Array of event posts
 */
export const getShopEvents = async (shopId) => {
  try {
    const eventsRef = collection(db, 'shops', shopId, 'communityPosts');
    
    // ✅ Use eventDate instead of createdAt (matches existing index)
    const q = query(
      eventsRef,
      where('isEvent', '==', true),
      orderBy('eventDate', 'desc') // ✅ CHANGED from createdAt
    );
    
    const snapshot = await getDocs(q);
    const events = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`📅 Loaded ${events.length} events for shop ${shopId}`);
    return events;
  } catch (error) {
    console.error('❌ Error loading events:', error);
    
    // Fallback: Load all events without ordering
    try {
      const fallbackRef = collection(db, 'shops', shopId, 'communityPosts');
      const fallbackQuery = query(fallbackRef, where('isEvent', '==', true));
      const fallbackSnapshot = await getDocs(fallbackQuery);
      const fallbackEvents = fallbackSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        // ✅ Sort in JavaScript instead
        .sort((a, b) => {
          const dateA = a.createdAt || '';
          const dateB = b.createdAt || '';
          return dateB.localeCompare(dateA);
        });
      
      console.log(`📅 Loaded ${fallbackEvents.length} events using fallback query`);
      return fallbackEvents;
    } catch (fallbackError) {
      console.error('❌ Fallback query also failed:', fallbackError);
      return [];
    }
  }
};

/**
 * Update an existing community post
 */
export const updateCommunityPost = async (shopId, postId, updates) => {
  try {
    const postRef = doc(db, 'shops', shopId, 'communityPosts', postId);
    const postSnap = await getDoc(postRef);
    
    if (!postSnap.exists()) {
      throw new Error('Post not found');
    }
    
    await updateDoc(postRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    });
    
    console.log('✅ Community post updated:', postId);
    return { id: postId, ...postSnap.data(), ...updates };
  } catch (error) {
    console.error('❌ Error updating community post:', error);
    throw error;
  }
};


/**
 * Get shop data by username
 * @param {string} username 
 * @returns {Promise<Object>}
 */
export const getShopByUsername = async (username) => {
  try {
    const shopsRef = collection(db, 'shops');
    const q = query(shopsRef, where('username', '==', username));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const shopDoc = querySnapshot.docs[0];
    return {
      id: shopDoc.id,
      ...shopDoc.data()
    };
  } catch (error) {
    console.error('Error getting shop by username:', error);
    throw error;
  }
};

// ============= UPDATE EXISTING FUNCTIONS =============

// UPDATE saveShopData to include services
export const saveShopData = async (userId, data) => {
  try {
    const hasShop = await checkExistingShop(userId);
    if (hasShop) {
      throw new Error('User already has a shop');
    }  
    
    console.log('Starting shop data save:', { userId, data });

    // Generate username if not provided
    let username = data.username;
    if (!username && data.name) {
      username = await generateUsername(data.name);
    }

    console.log('Home widgets being saved:', data.homeWidgets);

    // Clean data before saving to Firestore
    let shopData = {
      name: data.name || '',
      description: data.description || '',
      isRestaurant: data.isRestaurant || false,
      mission: data.mission || '',
      username: username,
      theme: cleanDataForFirestore(data.theme) || {},
      layout: cleanDataForFirestore(data.layout) || {},
      homeWidgets: data.homeWidgets || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      userId: userId,
      // Explicit from creation so the public page's plan gating never has to
      // guess — an absent field would otherwise be indistinguishable from a
      // legacy shop whose plan was never mirrored here. Overwritten by the
      // billing API's shop-doc update if the signup flow's plan pick is paid.
      plan: 'free',
      items: data.items?.map(item => ({
        id: item.id || Date.now().toString(),
        name: item.name || '',
        price: item.price || '',
        description: item.description || '',
        category: item.category || 'Other',
        quantity: item.quantity || 1,
        currentImageIndex: item.currentImageIndex || 0,
        address: item.address || '',
        coordinates: item.coordinates || null,
        tags: item.tags || [],
        images: [],
        isDigital: item.isDigital || false,
        digitalFile: null,
        isFeatured: item.isFeatured || false,
        customOptions: item.customOptions || []
      })) || [],
      // ADD: Services array
      services: data.services?.map(service => ({
        id: service.id || Date.now().toString(),
        name: service.name || '',
        price: service.price || '',
        description: service.description || '',
        category: service.category || 'Other',
        slots: service.slots || 1,
        currentImageIndex: service.currentImageIndex || 0,
        address: service.address || '',
        coordinates: service.coordinates || null,
        images: []
      })) || [],
      profile: null
    };

    console.log('Shop data with widgets:', {
      userId,
      username: shopData.username,
      name: shopData.name,
      widgetCount: shopData.homeWidgets?.length || 0,
      itemCount: shopData.items?.length || 0,
      serviceCount: shopData.services?.length || 0 // ADD log
    });

    // Save initial clean data
    const shopRef = doc(db, 'shops', userId);
    await setDoc(shopRef, shopData);

    // Profile image upload
    if (data.profile) {
      try {
        let profileUrl;

        if (typeof data.profile === 'string') {
          profileUrl = data.profile;
        } else if (data.profile.file instanceof File || data.profile.file instanceof Blob) {
          profileUrl = await uploadImageWithCORS(
            data.profile.file,
            `shops/${userId}/profile/profile-${Date.now()}`
          );
        }
      
        if (profileUrl) {
          shopData.profile = profileUrl;
          await updateDoc(shopRef, {
            profile: profileUrl,
            updatedAt: new Date().toISOString()
          });
          console.log('Profile image uploaded:', profileUrl);
        }
      } catch (profileError) {
        console.error('Profile upload failed:', profileError);
      }
    }

    // Item images upload
    // Item images upload section - REPLACE existing code
if (Array.isArray(data.items)) {
  const processedItems = await Promise.all(data.items.map(async (item) => {
    const processedItem = {
      id: item.id || Date.now().toString(),
      name: item.name || '',
      price: item.price || '',
      description: item.description || '',
      category: item.category || 'Other',
      currentImageIndex: item.currentImageIndex || 0,
      quantity: item.quantity || 1,
      address: item.address || '',
      coordinates: item.coordinates || null,
      tags: item.tags || [],
      images: [],
      isDigital: item.isDigital || false,
      digitalFile: null,
      isFeatured: item.isFeatured || false,
      customOptions: item.customOptions || []
    };

    if (Array.isArray(item.images)) {
      const imageUrls = await Promise.all(item.images.map(async (image, index) => {
        // Skip null/undefined images
        if (!image) return null;

        // If already a URL string, keep it
        if (typeof image === 'string') return image;

        // If it has a file property, upload it
        if (image?.file instanceof File || image?.file instanceof Blob) {
          try {
            return await uploadImageWithCORS(
              image.file,
              `shops/${userId}/items/${processedItem.id}/image-${index}-${Date.now()}`
            );
          } catch (error) {
            console.error(`Failed to upload image ${index}:`, error);
            return null;
          }
        }

        // If it's a File/Blob directly
        if (image instanceof File || image instanceof Blob) {
          try {
            return await uploadImageWithCORS(
              image,
              `shops/${userId}/items/${processedItem.id}/image-${index}-${Date.now()}`
            );
          } catch (error) {
            console.error(`Failed to upload image ${index}:`, error);
            return null;
          }
        }

        return null;
      }));

      processedItem.images = imageUrls.filter(Boolean);
    }

    // Upload digital product file if provided
    if (item.isDigital && item.digitalFile?.file instanceof File) {
      try {
        const storagePath = await uploadDigitalProduct(userId, processedItem.id, item.digitalFile.file);
        processedItem.digitalFile = {
          name: item.digitalFile.name,
          size: item.digitalFile.size,
          type: item.digitalFile.type,
          storagePath
        };
      } catch (err) {
        console.error('Failed to upload digital product file:', err);
      }
    } else if (item.isDigital && item.digitalFile?.storagePath) {
      // Already uploaded (existing item with storagePath)
      processedItem.digitalFile = {
        name: item.digitalFile.name,
        size: item.digitalFile.size,
        type: item.digitalFile.type,
        storagePath: item.digitalFile.storagePath
      };
    }

    return processedItem;
  }));
  
  shopData.items = processedItems;

  // Update items in Firestore
  await updateDoc(shopRef, {
    items: processedItems,
    updatedAt: new Date().toISOString()
  });

  console.log('✅ Items processed:', processedItems.length, 'Total images:', processedItems.reduce((acc, item) => acc + item.images.length, 0));
}

    if (Array.isArray(data.services)) {
  const processedServices = await Promise.all(data.services.map(async (service) => {
    const processedService = {
      id: service.id || Date.now().toString(),
      name: service.name || '',
      price: service.price || '',
      description: service.description || '',
      category: service.category || 'Other',
      currentImageIndex: service.currentImageIndex || 0,
      slots: service.slots || 1,
      address: service.address || '',
      coordinates: service.coordinates || null,
      images: []
    };
  
    if (Array.isArray(service.images)) {
      const imageUrls = await Promise.all(service.images.map(async (image, index) => {
        if (!image) return null;
        if (typeof image === 'string') return image;

        if (image?.file instanceof File || image?.file instanceof Blob) {
          try {
            return await uploadImageWithCORS(
              image.file,
              `shops/${userId}/services/${processedService.id}/image-${index}-${Date.now()}`
            );
          } catch (error) {
            console.error(`Failed to upload service image ${index}:`, error);
            return null;
          }
        }

        if (image instanceof File || image instanceof Blob) {
          try {
            return await uploadImageWithCORS(
              image,
              `shops/${userId}/services/${processedService.id}/image-${index}-${Date.now()}`
            );
          } catch (error) {
            console.error(`Failed to upload service image ${index}:`, error);
            return null;
          }
        }

        return null;
      }));

      processedService.images = imageUrls.filter(Boolean);
    }
  
    return processedService;
  }));
  
  shopData.services = processedServices;

  await updateDoc(shopRef, {
    services: processedServices,
    updatedAt: new Date().toISOString()
  });

  console.log('✅ Services processed:', processedServices.length);
}

    console.log('Shop data saved successfully with username:', shopData.username);

    // Mirror into the top-level items/services collections — best-effort,
    // never blocks/fails onboarding since the embedded arrays above are
    // already the source of truth and already saved.
    {
      const mirrorShopFields = buildShopFields(userId, shopData);
      mirrorItemsFull(userId, shopData.items, mirrorShopFields);
      mirrorServicesFull(userId, shopData.services, mirrorShopFields);
    }

    return { ...shopData, username };

  } catch (error) {
    console.error('Error in saveShopData:', error);
    throw error;
  }
};


/**
 * Get items with like status and view count
 */
export const getItemsLikeStatus = async (items, userIP) => {
  return items.map(item => ({
    ...item,
    isLiked: (item.likes || []).includes(userIP),
    likeCount: (item.likes || []).length,
    viewCount: (item.views || []).length || 0
  }));
};

export const getFeaturedItems = async (limitCount = 6) => {
  try {
    const querySnapshot = await getDocs(collection(db, 'shops'));

    let allItems = [];
    querySnapshot.docs.forEach(doc => {
      const shopData = doc.data();

      // ✅ Filter paused/deleted accounts
      if (shopData.accountStatus === 'paused' || shopData.accountStatus === 'deleted') {
        console.log('🚫 Skipping shop:', doc.id, 'Status:', shopData.accountStatus);
        return;
      }
      
      // ✅ NEW: Filter closed stores
      if (shopData.storeOpen === false) {
        console.log('🚫 Store closed:', doc.id);
        return;
      }

      if (shopData.hideFromWelcomePage === true) {
        return;
      }

      if (shopData?.items && Array.isArray(shopData.items)) {
        const shopItems = shopData.items
          .filter(item => {
            const hasImages = item.images && item.images.length > 0 && item.images.some(img => img);
            const hasValidPrice = item.price && !isNaN(parseFloat(item.price)) && parseFloat(item.price) > 0;
            const hasStock = !item.deleted && !item.hidden && (item.quantity == null || parseInt(item.quantity) > 0);
            const isVisible = item.visible !== false;
            const isNative = !item.importedFrom; // exclude imported copies

            return hasImages && hasValidPrice && hasStock && isVisible && isNative;
          })
          .map(item => ({
            ...item,
            shopId: doc.id,
            shopName: shopData.name || 'Unknown Shop',
            shopUsername: shopData.username,
            shopProfilePicture: shopData.profile,
            shopDescription: shopData.description || '',
            shopTheme: shopData.theme || {},
            shopStatus: shopData.accountStatus || 'active',
            accountStatus: shopData.accountStatus || 'active',
            storeOpen: shopData.storeOpen !== false,
            shopStoreType: shopData.storeType || null,
            shopFollowersCount: (shopData.followers || []).length,
            shopMasterAddresses: shopData.masterAddresses || [],
            currency: shopData.currency || 'USD',
            shopGuestCheckoutFields: shopData.guestCheckoutFields || null,
            quantity: item.quantity || 1
          }));
        allItems = [...allItems, ...shopItems];
      }
    });

    allItems.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return dateB - dateA;
    });

    return allItems.slice(0, limitCount);
  } catch (error) {
    console.error('Error fetching featured items:', error);
    throw error;
  }
};

// SAME FIX FOR getFeaturedServices:
export const getFeaturedServices = async (limitCount = 6) => {
  try {
    const querySnapshot = await getDocs(collection(db, 'shops'));

    let allServices = [];
    querySnapshot.docs.forEach(doc => {
      const shopData = doc.data();
      
      // ✅ FIXED
      if (shopData.accountStatus === 'paused' || shopData.accountStatus === 'deleted') {
        console.log('🚫 Skipping shop:', doc.id, 'Status:', shopData.accountStatus);
        return;
      }

      // ✅ NEW: Filter closed stores
      if (shopData.storeOpen === false) {
        console.log('🚫 Store closed:', doc.id);
        return;
      }

      if (shopData.hideFromWelcomePage === true) {
        return;
      }

      if (shopData?.services && Array.isArray(shopData.services)) {
        const shopServices = shopData.services
          .filter(service => {
            const hasImages = service.images && service.images.some(img => img);
            const hasContent = hasImages;
            const hasValidSlots = service.slots && !isNaN(parseInt(service.slots)) && parseInt(service.slots) > 0;
            const isActive = !service.deleted && !service.hidden;
            const isVisible = service.visible !== false;

            return hasContent && hasValidSlots && isActive && isVisible;
          })
          .map(service => ({
            ...service,
            shopId: doc.id,
            shopName: shopData.name || 'Unknown Shop',
            shopUsername: shopData.username,
            shopProfilePicture: shopData.profile,
            shopDescription: shopData.description || '',
            shopTheme: shopData.theme || {},
            shopStatus: shopData.accountStatus || 'active',
            accountStatus: shopData.accountStatus || 'active',
            storeOpen: shopData.storeOpen !== false,
            shopStoreType: shopData.storeType || null,
            shopFollowersCount: (shopData.followers || []).length,
            shopMasterAddresses: shopData.masterAddresses || [],
            currency: shopData.currency || 'USD',
            shopGuestCheckoutFields: shopData.guestCheckoutFields || null,
            slots: service.slots || 1
          }));
        allServices = [...allServices, ...shopServices];
      }
    });

    allServices.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return dateB - dateA;
    });

    return allServices.slice(0, limitCount);
  } catch (error) {
    console.error('Error fetching featured services:', error);
    throw error;
  }
};

/**
 * Real server-side-filtered/limited counterpart to getFeaturedItems, reading
 * the top-level `items` collection (see src/firebase/itemsCollectionSync.js)
 * instead of scanning every shop document.
 */
export const getFeaturedItemsFromCollection = async (limitCount = 6) => {
  try {
    const q = query(
      collection(db, 'items'),
      where('visible', '==', true),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(item => item.accountStatus !== 'paused' && item.accountStatus !== 'deleted' && item.storeOpen !== false && item.shopHideFromWelcomePage !== true);
  } catch (error) {
    console.error('Error fetching featured items from collection:', error);
    return [];
  }
};

/** Services counterpart of getFeaturedItemsFromCollection. */
export const getFeaturedServicesFromCollection = async (limitCount = 6) => {
  try {
    const q = query(
      collection(db, 'services'),
      where('visible', '==', true),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(service => service.accountStatus !== 'paused' && service.accountStatus !== 'deleted' && service.storeOpen !== false && service.shopHideFromWelcomePage !== true);
  } catch (error) {
    console.error('Error fetching featured services from collection:', error);
    return [];
  }
};

// UPDATE: saveInitialShop to include username and fix locations
// UPDATE saveInitialShop to handle services
export const saveInitialShop = async (userId, data) => {
  try {
    const batch = writeBatch(db);
    
    // Generate username if not provided
    let username = data.username;
    if (!username && data.name) {
      username = await generateUsername(data.name);
    }
    
    // 1. Create main shop document
    const shopRef = doc(db, 'shops', userId);
    const shopDoc = {
      ownerId: userId,
      name: data.name || '',
      description: data.description || '',
      mission: data.mission || '',
      isRestaurant: data.isRestaurant || false,
      username: username,
      theme: cleanDataForFirestore(data.theme) || {},
      layout: cleanDataForFirestore(data.layout) || {},
      status: 'active',
      profile: null,
      searchTerms: generateSearchTerms({ shopName: data.name }),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      stats: {
        items: 0,
        services: 0, // ADD services count
        views: 0,
        likes: 0
      }
    };

    batch.set(shopRef, shopDoc);

    // 2. Process items
    const items = [];
    const locations = [];

    if (Array.isArray(data.items)) {
      for (const item of data.items) {
        const itemId = `${userId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const itemRef = doc(db, 'items', itemId);

        // Process images
        const imageUrls = await Promise.all(item.images.map(async (image, index) => {
          if (!image?.file) return null;
          return await uploadImageWithCORS(
            image.file,
            `shops/${userId}/items/${itemId}/image-${index}-${Date.now()}`
          );
        }));

        const itemDoc = {
          id: itemId,
          shopId: userId,
          name: item.name || '',
          price: item.price || '',
          description: item.description || '',
          category: item.category || 'Other',
          images: imageUrls.filter(Boolean),
          status: 'active',
          address: item.address || '',
          coordinates: item.coordinates || null,
          quantity: item.quantity || 1,
          views: 0,
          likes: 0,
          deleted: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        batch.set(itemRef, itemDoc);
        items.push(itemDoc);

        // If item has location, create location document
        if (item.coordinates) {
          const locationRef = doc(db, 'locations', itemId);
          const locationDoc = {
            itemId,
            shopId: userId,
            address: item.address,
            coordinates: item.coordinates,
            createdAt: new Date().toISOString()
          };
          batch.set(locationRef, locationDoc);
          locations.push(locationDoc);
        }
      }
    }

    // ADD: 3. Process services
    const services = [];
    if (Array.isArray(data.services)) {
      for (const service of data.services) {
        const serviceId = `${userId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const serviceRef = doc(db, 'services', serviceId);

        // Process images
        const imageUrls = await Promise.all(service.images.map(async (image, index) => {
          if (!image?.file) return null;
          return await uploadImageWithCORS(
            image.file,
            `shops/${userId}/services/${serviceId}/image-${index}-${Date.now()}`
          );
        }));

        const serviceDoc = {
          id: serviceId,
          shopId: userId,
          name: service.name || '',
          price: service.price || '',
          description: service.description || '',
          category: service.category || 'Other',
          images: imageUrls.filter(Boolean),
          status: 'active',
          address: service.address || '',
          coordinates: service.coordinates || null,
          slots: service.slots || 1,
          views: 0,
          likes: 0,
          deleted: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        batch.set(serviceRef, serviceDoc);
        services.push(serviceDoc);

        // If service has location, create location document
        if (service.coordinates) {
          const locationRef = doc(db, 'locations', serviceId);
          const locationDoc = {
            serviceId,
            shopId: userId,
            address: service.address,
            coordinates: service.coordinates,
            createdAt: new Date().toISOString()
          };
          batch.set(locationRef, locationDoc);
          locations.push(locationDoc);
        }
      }
    }

    // 4. Upload and set profile image if exists
    if (data.profile?.file) {
      const profileUrl = await uploadImageWithCORS(
        data.profile.file,
        `shops/${userId}/profile/profile-${Date.now()}`
      );
      if (profileUrl) {
        shopDoc.profile = profileUrl;
      }
    }

    // 5. Update stats
    const statsRef = doc(db, 'stats', userId);
    batch.set(statsRef, {
      shopId: userId,
      views: 0,
      likes: 0,
      items: items.length,
      services: services.length, // ADD services count
      lastUpdated: new Date().toISOString()
    });

    // 6. Commit everything
    await batch.commit();

    console.log('Initial shop saved with username:', username);
    console.log('Items saved:', items.length);
    console.log('Services saved:', services.length); // ADD log

    return {
      ...shopDoc,
      items,
      services, // ADD services to return
      locations
    };

  } catch (error) {
    console.error('Error saving initial shop:', error);
    throw error;
  }
};

// UPDATE: batchUpdateShopData to preserve username
export const batchUpdateShopData = async (userId, updateData) => {
  try {
    console.log('Starting batch update for shop:', userId);
    
    const batch = writeBatch(db);
    const shopRef = doc(db, 'shops', userId);
    
    // Get current shop data to preserve username
    const currentShop = await getDoc(shopRef);
    const currentData = currentShop.data();
    
    // Clean the update data
    const cleanedData = cleanDataForFirestore({
      ...updateData,
      username: currentData?.username, // Preserve existing username
      updatedAt: new Date().toISOString()
    });
    
    // Update the main shop document
    batch.update(shopRef, cleanedData);
    
    // If updating items, we might need to update individual item documents
    if (updateData.items && Array.isArray(updateData.items)) {
      // Update stats
      const statsRef = doc(db, 'stats', userId);
      batch.update(statsRef, {
        items: updateData.items.filter(item => !item.deleted).length,
        lastUpdated: new Date().toISOString()
      });
    }
    
    // Commit all updates in a single batch
    await batch.commit();
    
    console.log('Batch update completed successfully');
    return true;
    
  } catch (error) {
    console.error('Error in batch update:', error);
    throw error;
  }
};

// Reverse-geocode coordinates → { city, address } using Nominatim
export const getAddressFromCoordinates = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`,
      { headers: { 'User-Agent': 'KalKode-Shop-App' } }
    );
    if (!response.ok) return { city: null, address: null };
    const data = await response.json();
    const a = data.address || {};
    const city    = a.city || a.town || a.village || a.county || null;
    const state   = a.state   || null;
    const country = a.country || null;
    const street   = [a.house_number, a.road].filter(Boolean).join(' ');
    const locality = a.city || a.town || a.village || a.neighbourhood || a.county;
    const region   = a.state && a.postcode ? `${a.state} ${a.postcode}` : (a.state || a.postcode);
    const parts    = [street, locality, region].filter(Boolean);
    const address  = parts.length ? parts.join(', ') : (data.display_name || null);
    return { city, state, country, address };
  } catch {
    return { city: null, address: null };
  }
};

// Add this helper function to get city from coordinates
export const getCityFromCoordinates = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?` +
      `lat=${lat}&lon=${lng}&` +
      `format=json&` +
      `addressdetails=1`,
      {
        headers: {
          'User-Agent': 'KalKode-Shop-App'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Geocoding service unavailable');
    }

    const data = await response.json();
    
    // Extract city from address components
    const city = data.address?.city || 
                 data.address?.town || 
                 data.address?.village || 
                 data.address?.county ||
                 'Unknown';
    
    return city;
  } catch (error) {
    console.error('Error getting city:', error);
    return null;
  }
};

// src/firebase/firebaseService.js - ADD these functions

// Follow/Unfollow a shop
// Add this to your firebaseService.js toggleFollowShop function
export const toggleFollowShop = async (currentUserId, targetShopId) => {
  try {
    console.log('📋 toggleFollowShop called:', { currentUserId, targetShopId });
    
    const userShopRef = doc(db, 'shops', currentUserId);
    const targetShopRef = doc(db, 'shops', targetShopId);
    
    // First, try to read the documents to see if they exist
    console.log('📖 Reading user shop...');
    const userShop = await getDoc(userShopRef);
    console.log('User shop exists:', userShop.exists());
    
    console.log('📖 Reading target shop...');
    const targetShop = await getDoc(targetShopRef);
    console.log('Target shop exists:', targetShop.exists());
    
    if (!userShop.exists() || !targetShop.exists()) {
      throw new Error('Shop not found');
    }
    
    const userFollowing = userShop.data().following || [];
    const targetFollowers = targetShop.data().followers || [];
    
    console.log('📊 Current state:', {
      userFollowing: userFollowing.length,
      targetFollowers: targetFollowers.length,
      isFollowing: userFollowing.includes(targetShopId)
    });
    
    const isFollowing = userFollowing.includes(targetShopId);
    const batch = writeBatch(db);
    
    if (isFollowing) {
      // Unfollow
      console.log('🔴 Unfollowing...');
      batch.update(userShopRef, {
        following: arrayRemove(targetShopId),
        updatedAt: new Date().toISOString()
      });
      batch.update(targetShopRef, {
        followers: arrayRemove(currentUserId),
        updatedAt: new Date().toISOString()
      });
    } else {
      // Follow
      console.log('🟢 Following...');
      batch.update(userShopRef, {
        following: arrayUnion(targetShopId),
        updatedAt: new Date().toISOString()
      });
      batch.update(targetShopRef, {
        followers: arrayUnion(currentUserId),
        updatedAt: new Date().toISOString()
      });
    }
    
    console.log('🚀 Committing batch...');
    await batch.commit();
    console.log('✅ Batch committed successfully');
    
    return !isFollowing;
  } catch (error) {
    console.error('❌ Detailed error in toggleFollowShop:', {
      name: error.name,
      message: error.message,
      code: error.code,
      details: error.details
    });
    throw error;
  }
};

// Increment engagement metrics
export const incrementEngagement = async (shopId, postId, metric) => {
  try {
    const shopRef = doc(db, 'shops', shopId);
    const shopSnap = await getDoc(shopRef);
    
    if (!shopSnap.exists()) return;
    
    const shopData = shopSnap.data();
    const posts = shopData.communityPosts || [];
    
    const updatedPosts = posts.map(post => {
      if (post.id === postId) {
        return {
          ...post,
          engagement: {
            ...post.engagement,
            [metric]: (post.engagement?.[metric] || 0) + 1
          }
        };
      }
      return post;
    });
    
    await updateDoc(shopRef, { communityPosts: updatedPosts });
  } catch (error) {
    console.error('Error incrementing engagement:', error);
  }
};

export const saveHomePageConfig = async (userId, widgets) => {
  try {
    const shopRef = doc(db, 'shops', userId);
    const cleanWidgets = widgets.map(widget => ({
      ...widget,
      config: cleanDataForFirestore(widget.config)
    }));
    
    await updateDoc(shopRef, {
      homeWidgets: cleanWidgets,
      homePageUpdated: new Date().toISOString()
    });
    
    return true;
  } catch (error) {
    console.error('Error saving home page config:', error);
    throw error;
  }
};

export const loadHomePageConfig = async (userId) => {
  try {
    const shopRef = doc(db, 'shops', userId);
    const shopDoc = await getDoc(shopRef);
    
    if (shopDoc.exists()) {
      const data = shopDoc.data();
      return data.homeWidgets || [];
    }
    
    return [];
  } catch (error) {
    console.error('Error loading home page config:', error);
    return [];
  }
};

export const checkExistingShop = async (userId) => {
  try {
    const shopRef = doc(db, 'shops', userId);
    const shopDoc = await getDoc(shopRef);
    return shopDoc.exists();
  } catch (error) {
    console.error('Error checking existing shop:', error);
    throw error;
  }
};

// Add this utility function to clean data before saving to Firestore
const cleanDataForFirestore = (data) => {
  if (!data) return data;
  
  if (Array.isArray(data)) {
    return data.map(item => cleanDataForFirestore(item));
  }
  
  if (typeof data === 'object' && !(data instanceof Date)) {
    const cleanedData = {};
    for (const [key, value] of Object.entries(data)) {
      // Skip file objects and null/undefined values
      if (value instanceof File || value === null || value === undefined) {
        continue;
      }
      cleanedData[key] = cleanDataForFirestore(value);
    }
    return cleanedData;
  }
  
  return data;
};

// Replace the uploadImageWithCORS function in firebaseService.js

const uploadImageWithCORS = async (file, path) => {
  if (!file) return null;
  
  try {
    // Direct upload without custom metadata initially
    const imageRef = ref(storage, path);
    
    // Upload the file
    const snapshot = await uploadBytes(imageRef, file, {
      contentType: file.type || 'image/jpeg',
      cacheControl: 'public, max-age=31536000',
    });
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    console.log('Image uploaded successfully:', downloadURL);
    return downloadURL;
    
  } catch (error) {
    console.error('Error uploading image:', error);
    
    // Fallback: Try with blob conversion
    try {
      const blob = await file.arrayBuffer();
      const imageRef = ref(storage, path);
      const snapshot = await uploadBytes(imageRef, blob, {
        contentType: file.type || 'image/jpeg'
      });
      return await getDownloadURL(snapshot.ref);
    } catch (fallbackError) {
      console.error('Fallback upload also failed:', fallbackError);
      return null;
    }
  }
};

// Add new function for image uploads
export const uploadShopImages = async (userId, data) => {
  try {
    const shopRef = doc(db, 'shops', userId);
    let updates = {};

    // Handle profile image
    if (data.profile?.file instanceof File) {
      try {
        const metadata = {
          contentType: data.profile.type || 'image/jpeg'
        };
        const profileRef = ref(storage, `shops/${userId}/profile/profile-${Date.now()}`);
        const profileSnapshot = await uploadBytes(profileRef, data.profile.file, metadata);
        updates.profile = await getDownloadURL(profileSnapshot.ref);
      } catch (error) {
        console.error('Profile upload failed:', error);
      }
    }

    // Handle item images
    if (Array.isArray(data.items)) {
      updates.items = await Promise.all(data.items.map(async (item) => {
        const processedItem = {
          id: item.id,
          name: item.name || '',
          price: item.price || '',
          description: item.description || '',
          currentImageIndex: item.currentImageIndex || 0,
          images: []
        };

        if (Array.isArray(item.images)) {
          const imageUrls = await Promise.all(item.images.map(async (image, index) => {
            if (!(image?.file instanceof File)) return null;
            
            try {
              const metadata = {
                contentType: image.type || 'image/jpeg'
              };
              const imageRef = ref(storage, `shops/${userId}/items/${item.id}/image-${index}-${Date.now()}`);
              const snapshot = await uploadBytes(imageRef, image.file, metadata);
              return await getDownloadURL(snapshot.ref);
            } catch (error) {
              console.error(`Failed to upload image ${index}:`, error);
              return null;
            }
          }));
          
          processedItem.images = imageUrls.filter(Boolean);
        }

        return processedItem;
      }));
    }

    // Clean updates before saving to Firestore
    const cleanUpdates = cleanDataForFirestore({
      ...updates,
      updatedAt: new Date().toISOString()
    });

    // Update Firestore with clean data
    if (Object.keys(cleanUpdates).length > 0) {
      await updateDoc(shopRef, cleanUpdates);
    }

    return true;
  } catch (error) {
    console.error('Error uploading images:', error);
    throw error;
  }
};

export const getNearbyShops = async (userLocation, excludeShopId, limit = 5) => {
  try {
    const shopsRef = collection(db, 'shops');
    const snapshot = await getDocs(shopsRef);

    const results = [];

    for (const shopDoc of snapshot.docs) {
      if (shopDoc.id === excludeShopId) continue;

      const data = shopDoc.data();
      if (data.accountStatus === 'paused' || data.accountStatus === 'deleted') continue;
      if (data.storeOpen === false) continue;

      const items = (data.items || []).filter(i => !i.deleted && i.visible !== false);
      if (items.length === 0) continue;

      let distanceMiles = null;
      if (userLocation?.latitude && userLocation?.longitude) {
        // Try to get a coordinate from the first item with one
        const refItem = items.find(i => i.coordinates?.lat && i.coordinates?.lng);
        if (refItem) {
          try {
            const meters = getDistance(
              { latitude: userLocation.latitude, longitude: userLocation.longitude },
              { latitude: refItem.coordinates.lat, longitude: refItem.coordinates.lng }
            );
            distanceMiles = parseFloat((meters / 1609.34).toFixed(1));
          } catch (_) {}
        }
      }

      results.push({
        id: shopDoc.id,
        name: data.name || 'Unknown Shop',
        profile: data.profile || null,
        theme: data.theme || {},
        items,
        distanceMiles,
      });
    }

    results.sort((a, b) => {
      if (a.distanceMiles === null && b.distanceMiles === null) return 0;
      if (a.distanceMiles === null) return 1;
      if (b.distanceMiles === null) return -1;
      return a.distanceMiles - b.distanceMiles;
    });

    return results.slice(0, limit);
  } catch (error) {
    console.error('Error getting nearby shops:', error);
    return [];
  }
};

export const getShopData = async (userId) => {
  try {
    const shopRef = doc(db, 'shops', userId);
    const shopSnap = await getDoc(shopRef);
    
    if (shopSnap.exists()) {
      return shopSnap.data();
    }
    
    return null;
  } catch (error) {
    console.error('Error getting shop data:', error);
    throw error;
  }
};

// Updated updateShopData to use batch operations
export const updateShopData = async (userId, updateData) => {
  try {
    // Use the new batch update function
    return await batchUpdateShopData(userId, updateData);
  } catch (error) {
    console.error('Error updating shop data:', error);
    throw error;
  }
};

// Optimized single field update (for real-time updates like view counts)
export const updateSingleField = async (userId, field, value) => {
  try {
    const shopRef = doc(db, 'shops', userId);
    await updateDoc(shopRef, {
      [field]: value,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error(`Error updating field ${field}:`, error);
    throw error;
  }
};

// Bulk operations for multiple shops (admin use)
export const bulkUpdateShops = async (updates) => {
  try {
    const batch = writeBatch(db);
    
    updates.forEach(({ userId, data }) => {
      const shopRef = doc(db, 'shops', userId);
      const cleanedData = cleanDataForFirestore({
        ...data,
        updatedAt: new Date().toISOString()
      });
      batch.update(shopRef, cleanedData);
    });
    
    await batch.commit();
    console.log(`Bulk update completed for ${updates.length} shops`);
    return true;
  } catch (error) {
    console.error('Error in bulk update:', error);
    throw error;
  }
};

// Search Operations
export const searchShops = async (searchTerm) => {
  try {
    const shopsRef = collection(db, 'shops');
    const q = query(
      shopsRef,
      where('status', '==', 'active'),
      where('searchTerms', 'array-contains', searchTerm.toLowerCase())
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error searching shops:', error);
    throw error;
  }
};

// Helper function to create search terms for better search functionality
export const generateSearchTerms = (shopData) => {
  const terms = new Set();
  
  // Add shop name terms
  if (shopData.shopName) {
    const words = shopData.shopName.toLowerCase().split(' ');
    words.forEach(word => terms.add(word));
  }

  // Add item names and tags
  if (shopData.items) {
    shopData.items.forEach(item => {
      if (item.name) {
        const words = item.name.toLowerCase().split(' ');
        words.forEach(word => terms.add(word));
      }
      if (item.tags) {
        item.tags.forEach(tag => terms.add(tag.toLowerCase()));
      }
    });
  }

  return Array.from(terms);
};

// Analytics and performance tracking
export const trackShopAnalytics = async (userId, action, metadata = {}) => {
  try {
    const analyticsRef = doc(db, 'analytics', `${userId}_${Date.now()}`);
    await setDoc(analyticsRef, {
      shopId: userId,
      action,
      metadata,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error tracking analytics:', error);
    // Don't throw - analytics shouldn't break the main flow
  }
};

// Cleanup functions for removing old data
export const cleanupOldImages = async (userId) => {
  try {
    // This would implement logic to remove unused images from storage
    // Implementation depends on your storage organization
    console.log(`Cleanup initiated for user ${userId}`);
    return true;
  } catch (error) {
    console.error('Error during cleanup:', error);
    throw error;
  }
};

// Export all functions
export default {
  getFeaturedItems,
  checkExistingShop,
  saveInitialShop,
  saveShopData,
  batchUpdateShopData,
  uploadShopImages,
  getShopData,
  updateShopData,
  updateSingleField,
  bulkUpdateShops,
  searchShops,
  generateSearchTerms,
  trackShopAnalytics,
  cleanupOldImages,
  getShopEvents 
};